import React, { useState, useEffect, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { FiShieldOff, FiAlertTriangle, FiX, FiUploadCloud } from "react-icons/fi";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";

const DAYS_WARNING_THRESHOLD = 7;
const CHECK_INTERVAL_MS = 4 * 60 * 60 * 1000; // 4 hours

export default function CertificateExpirationAlert({ onOpenCertificateModal, demoTutorialActive = false }) {
  const { isAuthenticated } = useAuth();
  const [alertData, setAlertData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const checkCertificates = useCallback(async () => {
    const projectId = localStorage.getItem('vidal_selected_project');
    if (!projectId || !isAuthenticated) return;

    // Check if user already dismissed for this session+project
    const dismissKey = `vidal_cert_alert_dismissed_${projectId}`;
    const dismissedAt = localStorage.getItem(dismissKey);
    if (dismissedAt) {
      const elapsed = Date.now() - parseInt(dismissedAt, 10);
      if (elapsed < CHECK_INTERVAL_MS) {
        setAlertData(null);
        return;
      }
    }

    try {
      const response = await api.get(`/certificates/list/${projectId}`);
      const certs = response.data?.data || response.data?.certificates || [];

      if (!Array.isArray(certs) || certs.length === 0) return;

      const now = new Date();
      const warningDate = new Date(now.getTime() + DAYS_WARNING_THRESHOLD * 24 * 60 * 60 * 1000);

      const expired = [];
      const expiring = [];

      for (const cert of certs) {
        const validTo = cert.validTo ? new Date(cert.validTo) : null;
        if (!validTo) continue;

        const cnpjFormatted = formatCNPJ(cert.cnpj);
        const rawName = cert.companyName || cert.commonName || cert.nome || cert.subject || cnpjFormatted;
        const companyName = rawName.includes(":") ? rawName.split(":")[0] : rawName;
        const daysLeft = Math.ceil((validTo - now) / (1000 * 60 * 60 * 24));

        if (validTo < now) {
          expired.push({ cnpj: cnpjFormatted, companyName, validTo, daysLeft });
        } else if (validTo <= warningDate) {
          expiring.push({ cnpj: cnpjFormatted, companyName, validTo, daysLeft });
        }
      }

      if (expired.length > 0 || expiring.length > 0) {
        setAlertData({ expired, expiring });
        setDismissed(false);
      } else {
        setAlertData(null);
      }
    } catch (err) {
      // Silently fail - don't block the user
      console.warn("[CertAlert] Erro ao verificar certificados:", err.message);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    // No modo demo, só verifica certificados depois que o tutorial terminar
    if (demoTutorialActive) return;
    // Small delay to ensure project data is loaded from localStorage
    const timer = setTimeout(() => {
      checkCertificates();
    }, 1500);
    return () => clearTimeout(timer);
  }, [isAuthenticated, checkCertificates, demoTutorialActive]);

  const handleDismiss = () => {
    setDismissed(true);
    const projectId = localStorage.getItem('vidal_selected_project');
    if (projectId) {
      localStorage.setItem(`vidal_cert_alert_dismissed_${projectId}`, String(Date.now()));
    }
  };

  const handleAction = () => {
    handleDismiss();
    // Navigate to certificates screen and open upload modal
    window.dispatchEvent(new CustomEvent('navigate-dashboard', { detail: 'certificates' }));
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('open-certificate-modal'));
    }, 300);
  };

  if (demoTutorialActive || !alertData || dismissed) return null;

  const { expired, expiring } = alertData;
  const hasExpired = expired.length > 0;

  return (
    <Overlay onClick={handleDismiss}>
      <Box onClick={(e) => e.stopPropagation()}>
        <CloseBtn onClick={handleDismiss}><FiX /></CloseBtn>

        <IconWrap $isExpired={hasExpired}>
          {hasExpired ? <FiShieldOff /> : <FiAlertTriangle />}
        </IconWrap>

        <Title>
          {hasExpired
            ? `Certificado${expired.length > 1 ? "s" : ""} Expirado${expired.length > 1 ? "s" : ""}`
            : `Certificado${expiring.length > 1 ? "s" : ""} Expirando`}
        </Title>

        <Description>
          {hasExpired
            ? `${expired.length} certificado${expired.length > 1 ? "s" : ""} digital${expired.length > 1 ? "is" : ""} expirou. O sistema não consegue consultar a SEFAZ para ess${expired.length > 1 ? "as empresas" : "a empresa"}.`
            : `${expiring.length} certificado${expiring.length > 1 ? "s" : ""} vai${expiring.length > 1 ? "ão" : ""} expirar nos próximos ${DAYS_WARNING_THRESHOLD} dias.`}
        </Description>

        <CertList>
          {expired.map((cert, i) => (
            <CertItem key={`exp-${i}`} $isExpired>
              <div className="cert-badge expired">EXPIRADO</div>
              <div className="cert-info">
                <span className="cert-name">{cert.companyName}</span>
                <span className="cert-cnpj">{cert.cnpj}</span>
              </div>
              <div className="cert-date">
                {Math.abs(cert.daysLeft)} dia{Math.abs(cert.daysLeft) !== 1 ? "s" : ""} atrás
              </div>
            </CertItem>
          ))}
          {expiring.map((cert, i) => (
            <CertItem key={`warn-${i}`}>
              <div className="cert-badge warning">{cert.daysLeft}d</div>
              <div className="cert-info">
                <span className="cert-name">{cert.companyName}</span>
                <span className="cert-cnpj">{cert.cnpj}</span>
              </div>
              <div className="cert-date">
                expira em {cert.daysLeft} dia{cert.daysLeft !== 1 ? "s" : ""}
              </div>
            </CertItem>
          ))}
        </CertList>

        <ButtonRow>
          <Btn onClick={handleDismiss}>
            Lembrar depois
          </Btn>
          <Btn $primary onClick={handleAction}>
            <FiUploadCloud />
            Atualizar Certificados
          </Btn>
        </ButtonRow>
      </Box>
    </Overlay>
  );
}

function formatCNPJ(cnpj) {
  if (!cnpj) return "";
  const digits = cnpj.replace(/\D/g, "");
  if (digits.length !== 14) return cnpj;
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
}

// ============ STYLED COMPONENTS ============

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  animation: ${fadeIn} 0.15s ease;
`;

const Box = styled.div`
  background: ${({ theme }) => theme.card || "#1a1c23"};
  border: 1px solid ${({ theme }) => theme.border || "rgba(255,255,255,0.08)"};
  border-radius: 16px;
  padding: 32px 28px;
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: ${slideUp} 0.2s ease;
  position: relative;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: none;
  background: ${({ theme }) =>
    theme.body === "#fafbfc" ? "rgba(0,0,0,0.04)" : "rgba(255,255,255,0.04)"};
  color: ${({ theme }) => theme.text || "#fff"};
  opacity: 0.5;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.95em;

  &:hover {
    opacity: 1;
    background: ${({ theme }) =>
      theme.body === "#fafbfc" ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.08)"};
  }
`;

const IconWrap = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: ${({ $isExpired }) =>
    $isExpired ? "rgba(239, 68, 68, 0.1)" : "rgba(245, 158, 11, 0.1)"};
  border: 1px solid
    ${({ $isExpired }) =>
      $isExpired ? "rgba(239, 68, 68, 0.2)" : "rgba(245, 158, 11, 0.2)"};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $isExpired }) => ($isExpired ? "#ef4444" : "#f59e0b")};
  font-size: 1.3em;
`;

const Title = styled.h4`
  margin: 0;
  font-size: 1.05em;
  font-weight: 600;
  color: ${({ theme }) => theme.text || "#fff"};
  text-align: center;
`;

const Description = styled.p`
  margin: 0;
  font-size: 0.85em;
  color: ${({ theme }) => theme.text || "#fff"};
  opacity: 0.6;
  text-align: center;
  line-height: 1.5;
`;

const CertList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 180px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.border || "rgba(255,255,255,0.08)"};
    border-radius: 2px;
  }
`;

const CertItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 10px;
  background: ${({ theme }) =>
    theme.body === "#fafbfc" ? "rgba(0,0,0,0.02)" : "rgba(255,255,255,0.02)"};
  border: 1px solid
    ${({ $isExpired, theme }) =>
      $isExpired
        ? "rgba(239, 68, 68, 0.15)"
        : theme.border || "rgba(255,255,255,0.04)"};

  .cert-badge {
    padding: 2px 8px;
    border-radius: 6px;
    font-size: 0.65em;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    flex-shrink: 0;

    &.expired {
      background: rgba(239, 68, 68, 0.12);
      color: #ef4444;
    }
    &.warning {
      background: rgba(245, 158, 11, 0.12);
      color: #f59e0b;
    }
  }

  .cert-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;

    .cert-name {
      font-size: 0.78em;
      font-weight: 500;
      color: ${({ theme }) => theme.text || "#fff"};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .cert-cnpj {
      font-size: 0.68em;
      color: ${({ theme }) => theme.text || "#fff"};
      opacity: 0.4;
      font-family: "SF Mono", "Consolas", monospace;
    }
  }

  .cert-date {
    font-size: 0.7em;
    color: ${({ $isExpired }) => ($isExpired ? "#ef4444" : "#f59e0b")};
    opacity: 0.8;
    flex-shrink: 0;
    white-space: nowrap;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  margin-top: 8px;
`;

const Btn = styled.button`
  flex: 1;
  height: 42px;
  padding: 0 20px;
  border-radius: 10px;
  font-size: 0.82em;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
  font-family: "Inter", system-ui, sans-serif;

  ${({ $primary, theme }) =>
    $primary
      ? `
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    border: none;
    color: #ffffff;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(245, 158, 11, 0.25);

    &:hover {
      background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
      box-shadow: 0 4px 16px rgba(245, 158, 11, 0.35);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
      box-shadow: 0 2px 6px rgba(245, 158, 11, 0.2);
    }

    svg {
      font-size: 1.1em;
      flex-shrink: 0;
    }
  `
      : `
    background: ${theme.body === "#fafbfc" ? "rgba(0,0,0,0.04)" : "rgba(255,255,255,0.06)"};
    border: 1px solid ${theme.border || "rgba(255,255,255,0.1)"};
    color: ${theme.text || "#fff"};
    opacity: 0.8;

    &:hover {
      opacity: 1;
      background: ${theme.body === "#fafbfc" ? "rgba(0,0,0,0.07)" : "rgba(255,255,255,0.1)"};
    }

    &:active {
      opacity: 0.9;
    }
  `}
`;
